import * as XLSX from 'xlsx';

/** A parsed spreadsheet: the header row + the data rows, all as trimmed strings. */
export interface ParsedSheet {
  headers: string[];
  rows: string[][];
}

// Hard byte ceiling before we hand the file to the parser. A legitimate glossary
// (≤ IMPORT_ROW_CAP rows) is well under 1 MB; this generous cap blunts the
// pathological-input surface of the spreadsheet parser (a crafted file can be
// slow/expensive to parse) without rejecting any real upload.
export const MAX_IMPORT_FILE_BYTES = 15 * 1024 * 1024;

/**
 * Parse a CSV / TSV / XLSX / XLS file client-side into a header row + data rows
 * (PLAT-10848 / E9, DS1). SheetJS auto-detects the format and (for delimited
 * text) the delimiter; only the first sheet is read. Every cell is coerced to a
 * trimmed string so downstream validation/mapping never deals with numbers or
 * `undefined`. Throws if the file can't be parsed — the caller treats that as a
 * fatal, whole-file rejection.
 */
export async function parseSheet(file: File): Promise<ParsedSheet> {
  if (file.size > MAX_IMPORT_FILE_BYTES) {
    throw new Error(
      `File is too large (${Math.round(file.size / 1024 / 1024)} MB). The limit is ${MAX_IMPORT_FILE_BYTES / 1024 / 1024} MB.`,
    );
  }
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return { headers: [], rows: [] };

  const sheet = workbook.Sheets[firstSheetName];
  // `header: 1` ⇒ array-of-arrays; `defval: ''` keeps ragged rows aligned so a
  // missing trailing cell reads as blank rather than dropping the column.
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: false,
  });

  const toStr = (cell: unknown): string =>
    cell == null ? '' : String(cell).trim();
  const table = matrix.map(row => row.map(toStr));

  const [headerRow, ...dataRows] = table;
  return {
    headers: headerRow ?? [],
    // Drop rows that are entirely empty (e.g. spacer rows at the end).
    rows: dataRows.filter(row => row.some(cell => cell !== '')),
  };
}
